import { useEffect, useRef, useState } from 'react';
import * as v from 'valibot';
import { HeaderBrandingSchema, HeaderConsumerSchema } from './schemas';

declare global {
  interface Window {
    core: {
      headerConfig?: HeaderConsumerSchema;
      headerBranding?: HeaderBrandingSchema;
    };
  }
}

const EventHeaderMenuConfig = v.literal('header.menu.config');
const EventHeaderMenuActive = v.literal('header.menu.active');
const EventHeaderNavigate = v.literal('header.navigate');
const EventHeaderOverlay = v.literal('header.overlay');
const EventHeaderBranding = v.literal('header.branding');
const EventDocumentClick = v.literal('document.click');
const EventHeaderChatInit = v.literal('header.chat.init');
const EventHeaderChatLoad = v.literal('header.chat.load');
const EventHeaderNotificationsInit = v.literal('header.notifications.init');
const EventHeaderNotificationsLoad = v.literal('header.notifications.load');
const EventHeaderTitle = v.literal('header.title');
const EventHeaderSearchInit = v.literal('header.search.init');
const EventHeaderSearchResult = v.literal('header.search.result');
const EventHeaderNotificationsOverride = v.literal(
  'header.notifications.override',
);
const EventFeedbackUI = v.literal('feedback.ui');
const EventFeedbackUIAction = v.literal('feedback.ui.action');
const EventUIErrorBase = v.union([
  v.literal('feedback.ui.error.base'),
  v.literal('native.ui.error.base'),
]);
const EventUIErrorBaseHttp = v.union([
  v.literal('feedback.ui.error.baseHttp'),
  v.literal('native.ui.error.baseHttp'),
]);
const EventUIErrorUnauthorized = v.union([
  v.literal('feedback.ui.error.unauthorized'),
  v.literal('native.ui.error.unauthorized'),
]);
const EventUIErrorUnhandled = v.union([
  v.literal('feedback.ui.error.unhandled'),
  v.literal('native.ui.error.unhandled'),
]);

const EventServicePing = v.literal('service.ping');
const EventServicePong = v.literal('service.pong');
const EventFeedbackUIWarningBase = v.literal('feedback.ui.warning.base');
const EventFeedbackUIInfoBase = v.literal('feedback.ui.info.base');
const EventFeedbackUISuccessBase = v.literal('feedback.ui.success.base');

const EventFeedbackUIPromptSingle = v.literal('feedback.ui.prompt.single');
const EventFeedbackUIPromptDual = v.literal('feedback.ui.prompt.dual');
const EventFeedbackUIPromptConfirm = v.literal('feedback.ui.prompt.confirm');
const EventFeedbackUIPromptReject = v.literal('feedback.ui.prompt.reject');

const VCoreEvents = v.union([
  EventHeaderMenuConfig,
  EventHeaderMenuActive,
  EventHeaderNavigate,
  EventHeaderOverlay,
  EventHeaderBranding,
  EventDocumentClick,
  EventHeaderChatInit,
  EventHeaderChatLoad,
  EventHeaderNotificationsInit,
  EventHeaderNotificationsLoad,
  EventHeaderTitle,
  EventHeaderSearchInit,
  EventHeaderSearchResult,
  EventHeaderNotificationsOverride,
  EventFeedbackUI,
  EventFeedbackUIAction,
  EventUIErrorBase,
  EventUIErrorBaseHttp,
  EventUIErrorUnauthorized,
  EventUIErrorUnhandled,
  EventServicePing,
  EventServicePong,
  EventFeedbackUIWarningBase,
  EventFeedbackUIInfoBase,
  EventFeedbackUISuccessBase,
  EventFeedbackUIPromptSingle,
  EventFeedbackUIPromptDual,
  EventFeedbackUIPromptConfirm,
  EventFeedbackUIPromptReject,
]);

export type TCoreEvents = v.Output<typeof VCoreEvents>;

type Event = {
  eventName: TCoreEvents;
  listener: EventListenerOrEventListenerObject;
};

export type HeaderEvents = Extract<TCoreEvents, `header.${string}`>;
export type FeedbackUIEvents = Extract<
  TCoreEvents,
  'feedback.ui' | `feedback.ui.${string}`
>;
export type FeedbackUIErrorEvents = Extract<
  TCoreEvents,
  'feedback.ui.error' | `feedback.ui.error${string}`
>;
export type FeedbackUIWarningEvents = Extract<
  TCoreEvents,
  'feedback.ui.warning' | `feedback.ui.warning${string}`
>;
export type FeedbackUIInfoEvents = Extract<
  TCoreEvents,
  'feedback.ui.info' | `feedback.ui.info${string}`
>;
export type FeedbackUISuccessEvents = Extract<
  TCoreEvents,
  'feedback.ui.success' | `feedback.ui.success${string}`
>;
export type FeedbackUIPromptEvents = Extract<
  TCoreEvents,
  `feedback.ui.prompt${string}`
>;

type EventData = {
  eventName: Event['eventName'];
  data: unknown;
};

const FeedbackUITypes = v.union([
  v.literal('modal'),
  v.literal('page'),
  v.literal('toast'),
  v.literal('portal'),
  v.literal('prompt'),
]);

const NativeUITypes = v.literal('native');

const UITypes = v.union([FeedbackUITypes, NativeUITypes]);

const FeedbackUIStatus = v.union([
  v.literal('error'),
  v.literal('warning'),
  v.literal('info'),
  v.literal('success'),
]);

const FeedbackUIActionBase = v.object({
  event: v.string(),
  type: v.optional(
    v.fallback(
      v.union([v.literal('event'), v.literal('link'), v.literal('close')]),
      'event',
    ),
  ),
  text: v.optional(v.object({ title: v.string() })),
});

const FeedbackUIAction = v.optional(FeedbackUIActionBase);

export const FeedbackUIEvent = v.object({
  consumer: v.string(),
  type: UITypes,
  status: FeedbackUIStatus,
  action: FeedbackUIAction,
});

export type FeedbackUIEvent = v.Output<typeof FeedbackUIEvent>;
export type FeedbackUITypes = v.Output<typeof FeedbackUITypes>;
export type FeedbackUIStatus = v.Output<typeof FeedbackUIStatus>;
export type FeedbackUIAction = v.Output<typeof FeedbackUIAction>;

export type AnalyticsEvent<PropertiesType = { [key: string]: string }> = {
  application: string;
  track: {
    event: {
      context: string;
      action: string;
    };
    properties?: PropertiesType;
  };
};

/**
 * Dispatches a custom event.
 * ___
 * - **eventName**: The name of the event to be dispatched.
 * - **data**: The data to be passed with the event.
 * ___
 * Example:
 * ```javascript
 * const eventData = {
 *     eventName: 'header.menu.active',
 *     data: { menuId: 'main-nav', status: 'active' }
 * };
 * publish(eventData);
 * ```
 */
export const publish = ({ eventName, data }: EventData) => {
  const event = new CustomEvent(eventName, { detail: data });
  window.dispatchEvent(event);
};

/**
 * Validates, enriches and dispatches a custom analytics event to our internal analytics service.
 * ___
 * **Analytics Event Structure:**
 * - **application**: Name of the application where the event occurred.
 * - **track.event.context**: Scenario or component within the application where the event occurred.
 * - **track.event.action**: Specific action that the user performed within the context.
 * - **track.properties**: Optional properties that provide more information about the event.
 * - **track.properties type** Generic, defaults to [key: string]: string
 *
 * **Note:** Although events are logged in **DEV** mode (but not sent), setting `window.logAnalytics` will output log data in **PROD**.
 * ___
 * Example:
 * ```javascript
 * const analyticsEvent = {
 *     application: 'Store',
 *     track: {
 *         event: {
 *             context: 'ProductPage',
 *             action: 'AddToCart'
 *         },
 *         properties: {
 *             productId: 'abc123',
 *             amount: 1
 *         }
 *     }
 * }
 *
 * Base usage:
 * publishAnalyticsEvent(analyticsEvent);
 *
 * With generic:
 * type TCustomProperties = {
 *    property: number,
 *    filters: ...
 * }
 * publishAnalyticsEvent<TCustomProperties>(analyticsEvent);
 * ```
 */
export const publishAnalyticsEvent = <PropertiesType>(
  analyticsEvent: AnalyticsEvent<PropertiesType>,
) => {
  const event = new CustomEvent('analytics.track', { detail: analyticsEvent });
  window.dispatchEvent(event);
};

/**
 * Sends a config to the header as a custom event.
 * Stores the config directly on the window as a composition fallback
 *
 */
export const publishHeaderConfig = (userConfig: HeaderConsumerSchema) => {
  const event = new CustomEvent('header.menu.config', { detail: userConfig });
  window.dispatchEvent(event);

  window.core = window.core || {};
  window.core.headerConfig = userConfig;
};

/**
 * Sends a branding config to the header as a custom event.
 * Stores the config directly on the window as a composition fallback
 *
 */
export const publishHeaderBranding = (userBranding: HeaderBrandingSchema) => {
  const event = new CustomEvent('header.branding', { detail: userBranding });
  window.dispatchEvent(event);

  window.core = window.core || {};
  window.core.headerBranding = userBranding;
};

/**
 * Custom React hook for subscribing to custom events.
 * ___
 * - **eventName**: The name of the event to subscribe to.
 * - **callback**: The callback function to execute when the event is triggered.
 *
 * This hook will automatically clean up and unsubscribe from all events upon component unmount.
 * ___
 * Example:
 * ```javascript
 * useSubscribe('header.menu.active', (e) => {
 *   console.log('Menu active event triggered:', e.detail);
 * });
 * ```
 */
export const useSubscribe = (
  eventName: Event['eventName'],
  callback: EventListenerOrEventListenerObject,
): void => {
  const eventRef = useRef<Event[]>([]);

  const subscribe = ({ eventName, listener }: Event) => {
    window.addEventListener(eventName, listener);
    eventRef.current.push({ eventName, listener });
  };

  const unsubscribe = ({ eventName, listener }: Event) => {
    window.removeEventListener(eventName, listener);
    eventRef.current = eventRef.current.filter(
      (evt) => evt.eventName !== eventName || evt.listener !== listener,
    );
  };

  useEffect(() => {
    return () => {
      eventRef.current.forEach(({ eventName, listener }) => {
        window.removeEventListener(eventName, listener);
      });
    };
  }, []);

  useEffect(() => {
    const event: Event = {
      eventName,
      listener: callback,
    };

    subscribe(event);

    return () => {
      unsubscribe(event);
    };
  }, [eventName, callback]);
};

/**
 * Error UI event
 * ___
 * Example:
 * ```javascript
 * const errorEvent = {
 *     type: 'modal'
 *     status: 'error',
 * }
 *
 * Base usage:
 * publishErrorEvent(errorEvent);
 *
 * With action and config:
 * const errorEvent = {
 *     type: 'modal'
 *     status: 'error',
 *     action: { event: 'foo' },
 *     config: {
 *         title: 'Error Title', description: 'Something went wrong'
 *      }
 * }
 * publishErrorEvent(errorEvent);
 * ```
 */
export const publishFeedbackEvent = (feedbackEvent: FeedbackUIEvent) => {
  const event = new CustomEvent('feedback.ui', { detail: feedbackEvent });
  window.dispatchEvent(event);
};

export const publishFeedbackActionEvent = (
  feedbackEvent: FeedbackUIEvent['action'],
) => {
  const event = new CustomEvent('feedback.ui.action', {
    detail: feedbackEvent,
  });
  window.dispatchEvent(event);
};

export const publishBaseErrorEvent = (errorEvent: BaseErrorEvent) => {
  const event = new CustomEvent('feedback.ui.error.base', {
    detail: errorEvent,
  });
  window.dispatchEvent(event);
};

export const publishPortalErrorEvent = (errorEvent: BasePortalErrorEvent) => {
  const event = new CustomEvent('feedback.ui.error.base', {
    detail: errorEvent,
  });
  window.dispatchEvent(event);
};

export const publishBaseHttpErrorEvent = (errorEvent: BaseHttpErrorEvent) => {
  const event = new CustomEvent('feedback.ui.error.baseHttp', {
    detail: errorEvent,
  });
  window.dispatchEvent(event);
};

export const publishUnauthorizedEvent = (
  errorEvent: ErrorUnauthorizedEvent,
) => {
  const event = new CustomEvent('feedback.ui.error.unauthorized', {
    detail: errorEvent,
  });
  window.dispatchEvent(event);
};

export const publishBaseWarningEvent = (warningEvent: BaseWarningEvent) => {
  const event = new CustomEvent('feedback.ui.warning.base', {
    detail: warningEvent,
  });
  window.dispatchEvent(event);
};

export const publishBaseInfoEvent = (infoEvent: BaseInfoEvent) => {
  const event = new CustomEvent('feedback.ui.info.base', { detail: infoEvent });
  window.dispatchEvent(event);
};

export const publishBaseSuccessEvent = (successEvent: BaseSuccessEvent) => {
  const event = new CustomEvent('feedback.ui.success.base', {
    detail: successEvent,
  });
  window.dispatchEvent(event);
};

export const publishUnhandledErrorEvent = (errorEvent: ErrorUnhandledEvent) => {
  const event = new CustomEvent('feedback.ui.error.unhandled', {
    detail: errorEvent,
  });
  window.dispatchEvent(event);
};

export const publishSingleOptionPrompt = (
  promptEvent: SingleActionPromptEvent,
) => {
  const event = new CustomEvent('feedback.ui.prompt.single', {
    detail: promptEvent,
  });
  window.dispatchEvent(event);
};

export const publishDualOptionPrompt = (promptEvent: DualActionPromptEvent) => {
  const event = new CustomEvent('feedback.ui.prompt.dual', {
    detail: promptEvent,
  });
  window.dispatchEvent(event);
};

const baseConfig = v.object({
  title: v.optional(v.string()),
  description: v.optional(v.string()),
  actionLabel: v.optional(v.string()),
});

const baseErrorConfig = baseConfig;

const baseHttpErrorConfig = v.object({
  title: v.optional(v.string()),
  description: v.optional(v.string()),
  statusCode: v.optional(v.string()),
  actionLabel: v.optional(v.string()),
});

export const BaseErrorEvent = v.merge([
  FeedbackUIEvent,
  v.object({
    event: EventUIErrorBase,
    config: v.optional(baseErrorConfig),
  }),
]);

export const UnhandledErrorEvent = v.merge([
  FeedbackUIEvent,
  v.object({
    event: EventUIErrorUnhandled,
    config: v.optional(baseErrorConfig),
  }),
]);

export const BasePortalErrorEvent = v.merge([
  FeedbackUIEvent,
  v.object({
    type: v.literal('portal'),
    status: v.literal('error'),
    target: v.string(),
    message: v.optional(v.string()),
    event: EventUIErrorBase,
    config: v.optional(baseErrorConfig),
  }),
]);

export const BaseWarningEvent = v.merge([
  FeedbackUIEvent,
  v.object({
    event: EventFeedbackUIWarningBase,
    config: v.optional(baseConfig),
  }),
]);

export const BaseInfoEvent = v.merge([
  FeedbackUIEvent,
  v.object({
    event: EventFeedbackUIInfoBase,
    config: v.optional(baseConfig),
  }),
]);

export const BaseSuccessEvent = v.merge([
  FeedbackUIEvent,
  v.object({
    event: EventFeedbackUISuccessBase,
    config: v.optional(baseConfig),
  }),
]);

export const BaseHttpErrorEvent = v.merge([
  FeedbackUIEvent,
  v.object({
    event: EventUIErrorBaseHttp,
    httpStatusCode: v.number(),
    config: v.optional(baseHttpErrorConfig),
  }),
]);

export const ErrorUnauthorizedEvent = v.merge([
  FeedbackUIEvent,
  v.object({
    event: EventUIErrorUnauthorized,
    config: v.optional(baseHttpErrorConfig),
  }),
]);

export const SingleActionPromptEvent = v.object({
  consumer: v.string(),
  event: EventFeedbackUIPromptSingle,
  type: FeedbackUITypes,
  promptType: v.literal('single'),
  title: v.string(),
  description: v.string(),
  action: FeedbackUIActionBase,
});

export const DualActionPromptEvent = v.object({
  consumer: v.string(),
  event: EventFeedbackUIPromptDual,
  type: FeedbackUITypes,
  promptType: v.literal('dual'),
  title: v.string(),
  description: v.string(),
  action: v.array(FeedbackUIActionBase),
});

export type BaseErrorEvent = v.Output<typeof BaseErrorEvent>;
export type BaseErrorConfig = v.Output<typeof baseErrorConfig>;

export type BaseErrorEventConfig = v.Output<typeof baseHttpErrorConfig>;
export type BasePortalErrorEvent = v.Output<typeof BasePortalErrorEvent>;

export type BaseHttpErrorEvent = v.Output<typeof BaseHttpErrorEvent>;
export type BaseHttpErrorEventConfig = v.Output<typeof baseHttpErrorConfig>;

export type ErrorUnauthorizedEvent = v.Output<typeof ErrorUnauthorizedEvent>;
export type ErrorUnauthorizedConfig = v.Output<typeof baseHttpErrorConfig>;

export type ErrorUnhandledEvent = v.Output<typeof UnhandledErrorEvent>;

export type BaseWarningEvent = v.Output<typeof BaseWarningEvent>;
export type BaseInfoEvent = v.Output<typeof BaseInfoEvent>;
export type BaseSuccessEvent = v.Output<typeof BaseSuccessEvent>;

export type SingleActionPromptEvent = v.Output<typeof SingleActionPromptEvent>;
export type DualActionPromptEvent = v.Output<typeof DualActionPromptEvent>;

/*
Service availability check
*/

type TAvailabilityServices = 'core.feedback.ui' | 'core.analytics';
type TUseCheckServiceAvailability = {
  serviceId: TAvailabilityServices;
  timeout?: number;
};

/**
 * useCheckServiceAvailability -React hook for checking service availability.
 * ___
 * Request side:
 * ```javascript
 * // Check if service is available by sending a ping event with serviceId
 * const availability = useCheckServiceAvailability({ serviceId: 'id', timeout: 1000});
 *
 * useEffect(() => {
 *   if (availability === false) {
 *     // Service not available
 *     // - Use a fallback or show an error message
 *   } else {
 *     // Service is available
 *     // - Store the availability in a state or use it in the component logic
 *   }
 * }, [availability]);
 * ```
 * ___
 * Receiver side (Service)
 * ```javascript
 * // Listen for service.ping event, and respond with service.pong if the service id matches.
 * window.addEventListener('service.ping', function (e) {
 *   if (e.detail && e.detail.serviceId === 'core.feedback.ui') {
 *     window.dispatchEvent(
 *       new CustomEvent('service.pong', {
 *         detail: {
 *           serviceId: 'core.feedback.ui',
 *         },
 *       }),
 *     );
 *   }
 * });
 * ```
 *
 * NOTE - Receiver side could also use the useSubscribe hook to listen for the service.ping event.
 * @param serviceId - The service id to check availability for (constraints to TAvailabilityServices type)
 * @param timeout - The timeout in milliseconds to wait for the service to respond
 */
export const useCheckServiceAvailability = ({
  serviceId,
  timeout = 4000,
}: TUseCheckServiceAvailability) => {
  const [availability, setAvailability] = useState<boolean | null>(null);

  const handleServicePong = (e: CustomEvent) => {
    if (e.detail && e.detail.serviceId === serviceId && availability === null) {
      setAvailability(true);
    }
  };

  useSubscribe('service.pong', (e) => handleServicePong(e as CustomEvent));

  useEffect(() => {
    if (availability === null) {
      publish({ eventName: 'service.ping', data: { serviceId } });
    }
    const checkAvailabilityTimeoutId = setTimeout(() => {
      if (availability === null) {
        setAvailability(false);
      }
    }, timeout);

    return () => {
      clearTimeout(checkAvailabilityTimeoutId);
    };
  }, [availability, serviceId, timeout]);

  return availability;
};

type TDualOptionPromptProps = {
  title: string;
  description: string;
  confirmText?: string;
  rejectText?: string;
  onConfirm: () => void;
  onReject: () => void;
};

/**
 * useOptionPrompt - React hook for showing a dual-option prompt (confirm/reject) and handling the corresponding actions.
 * ___
 * Example usage:
 * ```javascript
 * // Display a dual-option prompt
 * const { showPrompt } = useOptionPrompt({
 *   title: 'Are you sure?',
 *   description: 'Please confirm or cancel.',
 *   confirmText: 'Yes, proceed',
 *   rejectText: 'No, cancel',
 *   onConfirm: () => {
 *     console.log('Confirmed!');
 *   },
 *   onReject: () => {
 *     console.log('Rejected!');
 *   },
 * });
 *
 * // Trigger the prompt
 * onClick={showPrompt()};
 * ```
 * ___
 * Hook behavior:
 * - Publishes a prompt event (`feedback.ui.prompt.dual`) with the given title, description, and action buttons.
 * - Listens for confirm (`feedback.ui.prompt.confirm`) and reject (`feedback.ui.prompt.reject`) events.
 * - Executes the provided `onConfirm` callback when the confirm action is triggered.
 * - Executes the provided `onReject` callback when the reject action is triggered.
 * ___
 * @param title - The title of the prompt.
 * @param description - The description of the prompt.
 * @param confirmText - The text for the confirm button
 * @param rejectText - The text for the reject button
 * @param onConfirm - Callback function to execute when the confirm action is triggered.
 * @param onReject - Callback function to execute when the reject action is triggered.
 * @returns {Object} - An object containing `showDualOptionPrompt` to publish the prompt event.
 */
export const useOptionPrompt = ({
  title = 'Are you sure?',
  description = 'Please confirm or cancel this action.',
  confirmText = 'Confirm',
  rejectText = 'Cancel',
  onConfirm,
  onReject,
}: TDualOptionPromptProps) => {
  const uniqueKey = useRef(self.crypto.randomUUID());
  const confirmEvent = `feedback.ui.prompt.confirm.${uniqueKey.current}`;
  const rejectEvent = `feedback.ui.prompt.reject.${uniqueKey.current}`;

  const showPrompt = () => {
    const event = new CustomEvent(`feedback.ui.prompt.dual`, {
      detail: {
        consumer: 'playground',
        event: `feedback.ui.prompt.dual`,
        type: 'prompt',
        promptType: 'dual',
        title,
        description,
        action: [
          {
            text: { title: rejectText },
            event: rejectEvent,
          },
          {
            text: { title: confirmText },
            event: confirmEvent,
          },
        ],
      },
    });
    window.dispatchEvent(event);
  };

  useSubscribe(confirmEvent as 'feedback.ui.prompt.confirm', () => {
    onConfirm();
  });

  useSubscribe(rejectEvent as 'feedback.ui.prompt.reject', () => {
    onReject();
  });

  return { showPrompt };
};
